import { Capacitor } from '@capacitor/core';
import {
  LocalNotifications,
  type ScheduleOptions,
  type ScheduleResult,
} from '@capacitor/local-notifications';
import type { ReminderTimes } from './db';
import { getTenthStepCutoff, parseHm } from './timeUtils';

// Stable IDs per reminder slot. Capacitor uses int32; we partition the space:
//   1   morning check-in
//   2   noon check-in
//   3   evening check-in
//   100 10th-step nightly review
//   1000+ prayer times (one per HH:MM, hashed deterministically below)
const RESERVED_IDS = {
  morning: 1,
  noon: 2,
  evening: 3,
  tenthStep: 100,
} as const;

const PRAYER_ID_BASE = 1000;
const ALL_RESERVED_IDS = [
  RESERVED_IDS.morning,
  RESERVED_IDS.noon,
  RESERVED_IDS.evening,
  RESERVED_IDS.tenthStep,
];

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export async function ensureNativeNotificationPermission(): Promise<boolean> {
  if (!isNativePlatform()) return false;
  const result = await LocalNotifications.checkPermissions();
  if (result.display === 'granted') return true;
  if (result.display === 'denied') return false;
  const requested = await LocalNotifications.requestPermissions();
  return requested.display === 'granted';
}

function prayerId(time: string): number {
  // Map "HH:MM" to a stable id in [1000, 1000 + 24*60).
  const parsed = parseHm(time);
  if (!parsed) return PRAYER_ID_BASE;
  return PRAYER_ID_BASE + parsed.hour * 60 + parsed.minute;
}

function dedupeStrings(values: readonly string[]): string[] {
  return Array.from(new Set(values));
}

export interface ReminderScheduleInput {
  reminderTimes: ReminderTimes;
  notificationsEnabled: boolean;
}

export function buildScheduleOptions(
  input: ReminderScheduleInput,
): ScheduleOptions | null {
  if (!input.notificationsEnabled) return null;
  const { reminderTimes } = input;

  interface Slot {
    id: number;
    title: string;
    body: string;
    time: string;
  }
  const slots: Slot[] = [];

  if (reminderTimes.morning) {
    slots.push({
      id: RESERVED_IDS.morning,
      title: 'Morning check-in',
      body: 'Take a moment for your morning check-in.',
      time: reminderTimes.morning,
    });
  }
  if (reminderTimes.noon) {
    slots.push({
      id: RESERVED_IDS.noon,
      title: 'Noon check-in',
      body: 'Time for your noon check-in.',
      time: reminderTimes.noon,
    });
  }
  if (reminderTimes.evening) {
    slots.push({
      id: RESERVED_IDS.evening,
      title: 'Evening check-in',
      body: 'How did your day go? Log your evening check-in.',
      time: reminderTimes.evening,
    });
  }

  // 10th-step deadline at the evening slot (or 21:00 fallback).
  slots.push({
    id: RESERVED_IDS.tenthStep,
    title: '10th step inventory',
    body: 'Review your day before bed.',
    time: getTenthStepCutoff(reminderTimes),
  });

  // Dedupe prayer times — duplicate ids in `schedule({notifications})` have
  // undefined behavior on Android.
  for (const time of dedupeStrings(reminderTimes.prayer ?? [])) {
    slots.push({
      id: prayerId(time),
      title: 'Prayer reminder',
      body: 'A moment for prayer.',
      time,
    });
  }

  const notifications = slots.flatMap((slot) => {
    const parsed = parseHm(slot.time);
    if (!parsed) return [];
    return [
      {
        id: slot.id,
        title: slot.title,
        body: slot.body,
        schedule: {
          on: parsed,
          allowWhileIdle: true,
        },
      },
    ];
  });

  if (notifications.length === 0) return null;
  return { notifications };
}

async function getCurrentScheduledIds(): Promise<number[]> {
  const pending = await LocalNotifications.getPending();
  return pending.notifications.map((n) => Number(n.id)).filter(Number.isFinite);
}

/**
 * Replace the OS-level reminder schedule with whatever the current settings
 * imply. Cancels any reminders not in the new schedule, then schedules the
 * rest. Safe to call repeatedly — Capacitor de-dupes by id.
 */
export async function syncNativeReminders(
  input: ReminderScheduleInput,
): Promise<ScheduleResult | null> {
  if (!isNativePlatform()) return null;
  if (!(await ensureNativeNotificationPermission())) {
    return null;
  }

  const desired = buildScheduleOptions(input);
  const desiredIds = new Set(
    (desired?.notifications ?? []).map((n) => Number(n.id)),
  );

  // Cancel all of our reserved slots and any prayer-id slots we previously
  // owned but no longer want.
  const currentIds = await getCurrentScheduledIds();
  const toCancel = [
    ...ALL_RESERVED_IDS,
    ...currentIds.filter(
      (id) => id >= PRAYER_ID_BASE && id < PRAYER_ID_BASE + 24 * 60,
    ),
  ]
    .filter((id) => !desiredIds.has(id))
    .map((id) => ({ id }));

  if (toCancel.length > 0) {
    await LocalNotifications.cancel({ notifications: toCancel });
  }

  if (!desired) return null;
  return LocalNotifications.schedule(desired);
}

/**
 * Single entry point for "settings changed, please reconcile the OS schedule."
 * Cancels everything when notifications are disabled; otherwise calls the
 * incremental sync. Callers (notably SettingsProvider) don't have to know
 * the difference.
 */
export async function applyReminderSchedule(
  input: ReminderScheduleInput,
): Promise<void> {
  if (!isNativePlatform()) return;
  if (!input.notificationsEnabled) {
    await cancelAllNativeReminders();
    return;
  }
  await syncNativeReminders(input);
}

/**
 * Cancel every reminder this app owns, regardless of current settings. Used
 * when the user disables notifications entirely.
 */
export async function cancelAllNativeReminders(): Promise<void> {
  if (!isNativePlatform()) return;
  const currentIds = await getCurrentScheduledIds();
  const owned = currentIds.filter(
    (id) =>
      ALL_RESERVED_IDS.includes(id as (typeof ALL_RESERVED_IDS)[number]) ||
      (id >= PRAYER_ID_BASE && id < PRAYER_ID_BASE + 24 * 60),
  );
  if (owned.length === 0) return;
  await LocalNotifications.cancel({
    notifications: owned.map((id) => ({ id })),
  });
}
